"use client";

import { Area } from "@prisma/client";
import { Church, Mail, MapPin, Phone, User } from "lucide-react";

import { PageFormTemplate } from "../components/PageFormTemplate";
import { ServiceAreaSelector } from "../components/ServiceAreaSelector";

import {
  FormInput,
  FormRadioGroup,
  FormSelect,
} from "@/components/forms/components/FormInputs";
import { createVolunteerForPublicAction } from "@/lib/actions/public/volunteers";
import { EventKeys } from "@/lib/observability/catalog";
import {
  VolunteerFormData,
  volunteerSchema,
} from "@/lib/validations/volunteers";

interface VolunteerSignupFormProps {
  areas: Area[];
  organizationId: string;
  successRedirectUrl?: string;
}

/**
 * Public volunteer signup form component
 */
export function VolunteerSignupForm({
  areas,
  organizationId,
  successRedirectUrl = "/volunteer/thank-you",
}: VolunteerSignupFormProps) {
  // Default values for the form
  const defaultValues: VolunteerFormData = {
    active: true,
    church: "",
    city: "",
    country: "US",
    email: "",
    firstName: "",
    frequency: "daily",
    isSubscribed: true,
    lastName: "",
    organizationId: organizationId,
    phone: "",
    residenceAreaId: "",
    serviceAreas: [],
    state: "",
    streetAddress: "",
    zipCode: "",
  };

  // Form submission handler
  const handleFormSubmit = async (data: VolunteerFormData) => {
    // The server action already returns a standardized ActionResponse
    return await createVolunteerForPublicAction(data);
  };

  return (
    <PageFormTemplate
      callbackUrl={successRedirectUrl}
      cancelLabel="Cancel"
      defaultValues={defaultValues}
      description="Sign up to help with community needs in your selected areas"
      eventKeys={{
        attempt: EventKeys.PUBLIC_VOLUNTEER_SIGNUP_FORM_SUBMIT_ATTEMPT,
        failure: EventKeys.PUBLIC_VOLUNTEER_SIGNUP_FORM_SUBMIT_FAILURE,
        success: EventKeys.PUBLIC_VOLUNTEER_SIGNUP_FORM_SUBMIT_SUCCESS,
      }}
      formSchema={volunteerSchema}
      icon="UserPlus"
      submitLabel="Sign Up to Help"
      title="Helper Sign Up"
      onSubmit={handleFormSubmit}
    >
      {({ control, setValue, watch }) => {
        return (
          <div className="space-y-6">
            {/* Contact Information */}
            <div>
              <div className="my-4">
                <h3 className="text-md font-medium">Contact Information</h3>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput
                    required
                    aria-required="true"
                    control={control}
                    label="First Name"
                    name="firstName"
                    placeholder="Enter first name"
                    startContent={
                      <User
                        aria-hidden="true"
                        className="text-default-400 h-4 w-4"
                      />
                    }
                  />

                  <FormInput
                    required
                    aria-required="true"
                    control={control}
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter last name"
                    startContent={
                      <User
                        aria-hidden="true"
                        className="text-default-400 h-4 w-4"
                      />
                    }
                  />
                </div>

                <FormInput
                  required
                  aria-required="true"
                  control={control}
                  label="Email Address"
                  name="email"
                  placeholder="Enter email address"
                  startContent={
                    <Mail
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                  type="email"
                />

                <FormInput
                  control={control}
                  label="Phone Number"
                  name="phone"
                  placeholder="Enter phone number (optional)"
                  startContent={
                    <Phone
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />

                <FormInput
                  control={control}
                  label="Church Affiliation"
                  name="church"
                  placeholder="Enter church (optional)"
                  startContent={
                    <Church
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />
              </div>
            </div>

            {/* Address Information */}
            <div>
              <div className="my-4">
                <h3 className="text-md font-medium">Address Information</h3>
                <p className="text-sm text-default-600">
                  We collect address information to better coordinate help
                  services in your area.
                </p>
              </div>

              <div className="space-y-4">
                <FormInput
                  required
                  aria-required="true"
                  control={control}
                  label="Street Address"
                  name="streetAddress"
                  placeholder="Enter street address"
                  startContent={
                    <MapPin
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormInput
                    required
                    aria-required="true"
                    control={control}
                    label="City"
                    name="city"
                    placeholder="Enter city"
                    startContent={
                      <MapPin
                        aria-hidden="true"
                        className="text-default-400 h-4 w-4"
                      />
                    }
                  />

                  <FormInput
                    required
                    aria-required="true"
                    control={control}
                    label="State"
                    name="state"
                    placeholder="CA"
                    startContent={
                      <MapPin
                        aria-hidden="true"
                        className="text-default-400 h-4 w-4"
                      />
                    }
                  />

                  <FormInput
                    required
                    aria-required="true"
                    control={control}
                    label="ZIP Code"
                    name="zipCode"
                    placeholder="12345"
                    startContent={
                      <MapPin
                        aria-hidden="true"
                        className="text-default-400 h-4 w-4"
                      />
                    }
                  />
                </div>
              </div>
            </div>

            {/* Notification Preferences */}

            <div className="mb-3">
              <h4 className="font-medium">Email Notification Frequency</h4>
              <p className="text-sm text-default-500">
                How often would you like to receive notifications about needs?
              </p>
            </div>
            <FormRadioGroup
              control={control}
              data-testid="frequency-radio-group"
              name="frequency"
              options={[
                {
                  label:
                    "Immediate - Get ALL needs right away (plus you'll get critical needs like everyone else)",
                  value: "immediate",
                },
                {
                  label:
                    "Daily Summary - One email per day with new needs (plus critical needs immediately)",
                  value: "daily",
                },
                {
                  label:
                    "Weekly Digest - Weekly summary of all open needs (plus critical needs immediately)",
                  value: "weekly",
                },
              ]}
            />

            {/* Residence Area */}
            <div className="my-4">
              <h3 className="text-md font-medium">Area Information</h3>
              <p className="text-sm text-default-600">
                Tell us where you live and where you're willing to help
              </p>
            </div>

            <div className="space-y-4">
              {/* Residence Area */}
              <div>
                <FormSelect
                  required
                  aria-required="true"
                  control={control}
                  data-testid="residence-area-select"
                  label="Where do you live?"
                  name="residenceAreaId"
                  options={[
                    ...areas.map((area) => ({
                      label: `${area.name}, ${area.state}`,
                      value: area.id,
                    })),
                    {
                      label: "Other (please specify)",
                      value: "other",
                    },
                  ]}
                  placeholder="Select your area of residence"
                />
                {watch("residenceAreaId") === "other" && (
                  <div className="mt-3">
                    <FormInput
                      required
                      control={control}
                      data-testid="custom-residence-area-input"
                      label="Please specify your area of residence"
                      name="customResidenceArea"
                      placeholder="e.g., Springfield, IL"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Areas of Service - Most important aspect of a helper */}
            <div className="space-y-3 my-4">
              <ServiceAreaSelector
                areas={areas}
                data-testid="service-area-selector"
                selectedAreaIds={watch("serviceAreas") || []}
                onSelectionChange={(areaIds) =>
                  setValue("serviceAreas", areaIds, { shouldValidate: true })
                }
              />
            </div>
          </div>
        );
      }}
    </PageFormTemplate>
  );
}
